import { PartialProduct } from "@shared/types";
import cx from "classnames";
import { Button } from "components/Button";
import { CustomDatoImage } from "components/CustomDatoImage/CustomDatoImage";
import { Alert } from "components/common/Alert/Alert";
import { BrandFeatureIconSvg } from "components/icons/BrandFeatureIconSvg";
import { ChevronSvg, PagePointDirections } from "components/icons/ChevronSvg";
import { Spinner } from "components/spinner/Spinner";
import { AppContext } from "context/app";
import { CartContext } from "context/cart";
import { LocalizationContext } from "context/localization";
import { useTranslationContext } from "context/translation";
import { getEcommerceWithValue } from "helpers/analyticsLogger";
import { getYotpoInstanceId } from "helpers/yotpo";
import { useCartAnalyticsEvents } from "hooks/analytics/useCartAnalyticsEvents";
import { useProductsCylinderAnalytics } from "hooks/analytics/useProductsCylinderAnalytics";
import { useRouter } from "next/router";
import { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { Image } from "react-datocms/image";
import {
  ProductBySlugQuery,
  ResponsiveImage,
} from "services/datocms/generated";
import {
  GetItemsByGtinQuery,
  ItemByGtinQuery,
  useGetItemsByGtinQuery,
} from "services/graphql/generated";
import { Cylinder, CylinderType } from "./Cylinder";
import st from "./product-details-cylinder.module.scss";

export enum Steps {
  selectType,
  selectQuantity,
}

interface DeliveryFrequency {
  periodLengthNumber: number;
  periodLengthText: string;
}

interface ProductDetailsCylinderProps {
  datoProduct: ProductBySlugQuery["product"] | null;
  pepDirectProduct: ItemByGtinQuery["itemByGtin"] | null;
  selectedImage: number;
  setSelectedImage: (index: number) => void;
}

export const ProductDetailsCylinder: FC<ProductDetailsCylinderProps> = ({
  datoProduct,
  pepDirectProduct,
  selectedImage,
  setSelectedImage,
}) => {
  const { formatPriceByLocale } = useContext(LocalizationContext);
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.selectType);
  const [selectedCylinderType, setSelectedCylinderType] =
    useState<CylinderType | null>(() => datoProduct?.cylinderTypes[0] ?? null);
  const [showQuantityDropdown, setShowQuantityDropdown] =
    useState<boolean>(false);
  const [selectedQuantity, setSelectedQuantity] = useState<number | null>(null);
  const [selectedFrequency, setSelectedFrequency] =
    useState<DeliveryFrequency | null>(null);
  const [extraCylinderAdded, setExtraCylinderAdded] = useState<boolean>(false);
  const [cartLoading, setCartLoading] = useState(false);
  const { comingSoon, selectOrderQuantity } = useTranslationContext();
  const { tenant } = useContext(AppContext);
  const currency = tenant?.checkout?.paymentOptions?.defaultCurrencyCode || "";
  const { updateCartItem, updateCartWithMultipleItems } =
    useContext(CartContext);
  const { addToCartEvent } = useCartAnalyticsEvents();

  const allCylindersGtin: string[] = [];
  datoProduct?.cylinderTypes.forEach((cylinder) => {
    if (cylinder.gtin) {
      allCylindersGtin.push(cylinder.gtin);
    }
    if (cylinder.spareCylinder) {
      allCylindersGtin.push(cylinder.spareCylinder.gtin ?? "");
    }
  });

  const { data: allCylindersData, loading } = useGetItemsByGtinQuery({
    variables: {
      gtins: allCylindersGtin,
    },
  });

  const { itemsForLogging, getContinueButtonAnalytics } =
    useProductsCylinderAnalytics(
      currency,
      allCylindersData,
      datoProduct?.category?.analyticsCategoryName,
    );

  const selectedCylinderData = allCylindersData?.items?.items.find(
    (item) => item.gtin === selectedCylinderType?.gtin,
  );

  const extraCylinderData = allCylindersData?.items?.items.find(
    (item) => item.gtin === selectedCylinderType?.spareCylinder?.gtin,
  );

  useEffect(() => {
    if (!selectedFrequency && datoProduct?.cylinderFrequencyOptions) {
      const oneTimeOption = datoProduct?.cylinderFrequencyOptions?.find(
        (o) => !o.periodLengthNumber,
      );
      setSelectedFrequency({
        periodLengthNumber: oneTimeOption?.periodLengthNumber || 0,
        periodLengthText: oneTimeOption?.periodLengthText || "",
      });
    }
  }, [datoProduct?.cylinderFrequencyOptions, selectedFrequency]);

  const {
    quickconnectFeatureText,
    ordering,
    edit,
    selectCylinderType,
    each,
    orderQuantity,
    addAnExtraCylinderToMyOrderFor,
    addToCartFor,
    pleaseSelectACylinder,
    continue: continueText,
    returnEmptyCylinder,
  } = useTranslationContext();

  const router = useRouter();
  const handleAddCylinderToCart = async (
    productId: string,
    quantity: number,
    extraCylinder?: NonNullable<GetItemsByGtinQuery["items"]>["items"][number],
  ) => {
    setCartLoading(true);
    if (extraCylinder) {
      const ecommerce = getEcommerceWithValue({
        currency,
        itemListName: undefined,
        items: itemsForLogging
          .filter((item) => [productId, extraCylinder.id].includes(item.itemId))
          .map((item) => {
            if (item.itemId === productId) {
              return {
                ...item,
                quantity,
              };
            } else {
              return item;
            }
          }),
      });

      const newCart = await updateCartWithMultipleItems([
        {
          productId,
          quantity,
          subscriptionIntervalInDays: null,
          includeExistingQuantity: true,
        },
        {
          productId: extraCylinder.id,
          quantity: 1,
          subscriptionIntervalInDays: null,
          analyticsDetails: ecommerce,
        },
      ]);
      await addToCartEvent(newCart, ecommerce);
    } else {
      const analyticsItem = itemsForLogging.find(
        (item) => item.itemId === productId,
      ) as PartialProduct;
      analyticsItem.quantity = quantity;
      const ecommerce = getEcommerceWithValue({
        currency,
        itemListName: undefined,
        items: [analyticsItem],
      });
      const newCart = await updateCartItem({
        productId,
        quantity,
        subscriptionIntervalInDays: null,
        analyticsDetails: ecommerce,
        includeExistingQuantity: true,
      });
      await addToCartEvent(newCart, ecommerce);
    }
    router.push("/cart");

    const handleRouteChangeComplete = () => {
      setCartLoading(false);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
  };

  const handleContinueButton = () => {
    if (currentStep === Steps.selectQuantity && !cartLoading) {
      handleAddCylinderToCart(
        selectedCylinderData?.id,
        selectedQuantity || 0,
        extraCylinderAdded ? extraCylinderData : undefined,
      );
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleResetSelection = () => {
    setCurrentStep(Steps.selectType);
    setSelectedQuantity(null);
    setSelectedFrequency(null);
    setExtraCylinderAdded(false);
  };

  const handleToggleExtraCylinder = (event: ChangeEvent<HTMLInputElement>) => {
    setExtraCylinderAdded(event.target.checked);
  };

  const outOfStock =
    selectedCylinderData &&
    (selectedCylinderData?.isOutOfStock || !selectedCylinderData?.inventoryQty);

  const currentPrice =
    (selectedQuantity &&
      selectedCylinderData?.price &&
      selectedQuantity * selectedCylinderData?.price +
        (extraCylinderAdded ? 1 : 0) * (extraCylinderData?.price || 0)) ||
    0;
  const continueButtonDisabled =
    (currentStep === Steps.selectType && !selectedCylinderType) ||
    (currentStep === Steps.selectQuantity && !selectedQuantity);

  const renderButtonText = () => {
    if (outOfStock) {
      return comingSoon;
    }

    if (currentStep === Steps.selectType && !selectedCylinderType) {
      return pleaseSelectACylinder;
    }

    if (
      currentStep === Steps.selectQuantity &&
      !cartLoading &&
      selectedQuantity
    ) {
      return `${addToCartFor} ${formatPriceByLocale(currentPrice)}`;
    }

    if (cartLoading) {
      return <Spinner color="white" dotSize="10px" />;
    }

    return continueText?.toUpperCase();
  };

  return (
    <>
      <div className="bg-whiteSmoke mt-[-150px] pt-[160px] md:pt-[180px] md:pb-[75px]">
        <div className="container">
          <div>
            <h1 className="text-[#002238] font-[Poppins] text-[34px] md:text-[42px] font-bold leading-[38px] md:leading-[65px]">
              {datoProduct?.title}
            </h1>
            {datoProduct?.shortDescription && (
              <div className="text-[#002238] font-[Poppins] text-[14px] font-medium mb-[7px]">
                {datoProduct?.shortDescription}
              </div>
            )}
          </div>
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex flex-col md:w-3/5 h-full">
              <div className="md:h-10">
                {pepDirectProduct?.gtin && (
                  <div
                    className="yotpo-widget-instance"
                    data-yotpo-instance-id={getYotpoInstanceId("stars")}
                    data-yotpo-product-id={pepDirectProduct?.gtin}
                  />
                )}
              </div>
              <div className="w-full h-[362px] md:h-[424px] hidden md:flex justify-center bg-white rounded-[30px]">
                <div className="w-full flex justify-center">
                  {datoProduct?.productImages?.[selectedImage]
                    .responsiveImage && (
                    <Image
                      data={
                        datoProduct?.productImages?.[selectedImage]
                          .responsiveImage as ResponsiveImage
                      }
                      pictureClassName="object-contain"
                    />
                  )}
                </div>
              </div>
              {datoProduct?.productImages &&
                datoProduct?.productImages.length > 1 && (
                  <div className="w-full hidden md:flex justify-center items-center gap-2 mt-4 p-1">
                    {datoProduct?.productImages.map((image, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedImage(index)}
                        className={cx(
                          "w-[82px] relative cursor-pointer overflow-hidden bg-white rounded-[8px] md:rounded-[18px]",
                          index === selectedImage &&
                            "outline outline-2 outline-sodaStreamBlue",
                        )}
                      >
                        <Image
                          objectFit="contain"
                          layout="responsive"
                          pictureClassName="w-full aspect-square"
                          data={image.responsiveImage as ResponsiveImage}
                        />
                      </div>
                    ))}
                  </div>
                )}
            </div>
            <div className="flex flex-col justify-start h-full w-full md:w-3/5 relative md:px-0">
              <div className="flex items-end">
                <h2 className="text-[#002238] font-[Poppins] text-[18px] font-[600] leading-[27px] h-10">
                  {currentStep === Steps.selectType && selectCylinderType}
                </h2>
              </div>
              <div className="flex flex-col items-center">
                <div className="flex items-stretch w-full gap-4 md:justify-center">
                  {currentStep === Steps.selectType ? (
                    datoProduct?.cylinderTypes?.map((cylinderType, index) => (
                      <Cylinder
                        key={cylinderType.gtin}
                        cylinder={cylinderType}
                        setSelectedCylinderType={setSelectedCylinderType}
                        selectedCylinderType={selectedCylinderType}
                        loading={loading}
                        formattedPrice={formatPriceByLocale(
                          allCylindersData?.items?.items.find(
                            (item) => item.gtin === cylinderType?.gtin,
                          )?.price,
                        )}
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-start w-full p-4 bg-white rounded-2xl relative">
                      <div className="flex items-center justify-between w-full">
                        <h3 className="text-[#002238] font-[Poppins] text-base font-medium">
                          {ordering}
                        </h3>
                        <span
                          className="text-primary font-[Poppins] text-xs cursor-pointer font-medium underline"
                          onClick={handleResetSelection}
                          bi-type="Subscription summary - Ordering"
                          bi-id="Edit"
                        >
                          {edit}
                        </span>
                      </div>
                      <div className="flex items-center justify-start mt-2 relative">
                        {selectedQuantity &&
                          currentStep === Steps.selectQuantity && (
                            <div className={st.selectedQuantity}>
                              <span>{selectedQuantity}</span>
                            </div>
                          )}
                        <div className="w-[79px] shrink-0 bg-freshBlue rounded overflow-hidden">
                          {selectedCylinderType?.image?.responsiveImage && (
                            <CustomDatoImage
                              width={80}
                              data={
                                selectedCylinderType?.image?.responsiveImage
                              }
                            />
                          )}
                        </div>
                        <div className="ml-[14px]">
                          <h3 className="text-[#002238] font-[Poppins] text-sm font-normal">
                            {datoProduct?.title} {selectedCylinderType?.title}
                          </h3>
                          <h4 className="text-[#595858] font-[Poppins] text-[15px] font-light leading-[22px]">
                            {formatPriceByLocale(
                              selectedCylinderData?.price || 0,
                            )}{" "}
                            {each}
                          </h4>
                          {selectedCylinderType?.lowestPriceText && (
                            <p className="flex flex-col mt-1 text-gray-500 font-poppins text-xs font-medium leading-normal">
                              {selectedCylinderType?.lowestPriceText}
                            </p>
                          )}
                        </div>
                      </div>
                      {extraCylinderAdded &&
                        currentStep === Steps.selectQuantity && (
                          <div className="flex items-center justify-start mt-[25px] relative">
                            <div className={st.selectedQuantity}>
                              <span>1</span>
                            </div>
                            <div className="w-[79px] shrink-0 bg-freshBlue rounded overflow-hidden">
                              <Image
                                data={
                                  selectedCylinderType?.spareCylinder?.image
                                    ?.responsiveImage as ResponsiveImage
                                }
                              />
                            </div>
                            <div className="ml-[14px]">
                              <h3 className="text-[#002238] font-[Poppins] text-sm font-normal">
                                {selectedCylinderType?.spareCylinder?.title}
                              </h3>
                              <h4 className="text-[#595858] font-[Poppins] text-[15px] font-light leading-[22px]">
                                {formatPriceByLocale(
                                  extraCylinderData?.price || 0,
                                )}
                              </h4>
                              {selectedCylinderType?.spareCylinder
                                ?.lowestPriceText && (
                                <p className="flex flex-col mt-1 text-gray-500 font-poppins text-xs font-medium leading-normal">
                                  {
                                    selectedCylinderType?.spareCylinder
                                      .lowestPriceText
                                  }
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
              {currentStep === Steps.selectQuantity && (
                <>
                  <h3 className="text-[#002238] font-[Poppins] text-[18px] font-[600] leading-[27px] my-4">
                    {orderQuantity}
                  </h3>
                  <div>
                    <div
                      className={cx(
                        "flex flex-col items-start absolute bg-white z-[1] md:z-0 p-4 w-full justify-between cursor-pointer rounded-2xl",
                        st.dropdown,
                      )}
                      onClick={() => setShowQuantityDropdown((prev) => !prev)}
                    >
                      <div className="flex items-center justify-between w-full">
                        <h3
                          className={cx(
                            "font-[Poppins] text-[15px] font-medium",
                            {
                              "text-[#002238]":
                                selectedQuantity && !showQuantityDropdown,
                              "text-[#595858]":
                                !selectedQuantity || showQuantityDropdown,
                            },
                          )}
                          onClick={() => setSelectedQuantity(null)}
                        >
                          {selectedQuantity && !showQuantityDropdown
                            ? `${selectedQuantity} Cylinder`
                            : selectOrderQuantity || "Select order quantity"}
                        </h3>
                        <ChevronSvg
                          stroke="black"
                          height={15}
                          width={15}
                          pointDirection={PagePointDirections.down}
                        />
                      </div>
                      {showQuantityDropdown && (
                        <>
                          {datoProduct?.cylinderQuantityOptions?.map(
                            (quantityOption, index) => (
                              <h3
                                key={index}
                                className="text-[#002238] font-[Poppins] text-sm font-medium pt-4 w-full"
                                onClick={() =>
                                  setSelectedQuantity(quantityOption.amount)
                                }
                                bi-type="Co2 Cylinder - Order quantity"
                                bi-id={`${quantityOption.amount} Cylinder`}
                              >
                                {String(quantityOption.amount)}{" "}
                                {quantityOption.extraText && (
                                  <span className="text-sodaStreamBlue font-semibold">
                                    {quantityOption.extraText}
                                  </span>
                                )}
                              </h3>
                            ),
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className={st.extraCylinder}>
                    {selectedQuantity && (
                      <Alert fullWidth type="info">
                        <p className="text-[13px] font-medium leading-[17px]">
                          {returnEmptyCylinder?.replace(
                            "{value}",
                            selectedQuantity.toString(),
                          )}
                        </p>
                      </Alert>
                    )}
                    <div className={st.extraCylinderCheckbox}>
                      <input
                        onChange={handleToggleExtraCylinder}
                        type="checkbox"
                        className="mr-2 h-6 w-6"
                      />
                      <label className="">
                        {addAnExtraCylinderToMyOrderFor}{" "}
                        {formatPriceByLocale(extraCylinderData?.price || 0)}
                      </label>
                    </div>
                  </div>
                </>
              )}
              <Button
                className={cx(
                  "self-center text-[14px] w-3/4 min-w-[75%] py-4 h my-8",
                )}
                disabled={continueButtonDisabled || outOfStock || cartLoading}
                onClick={handleContinueButton}
                size="medium"
                {...getContinueButtonAnalytics(currentStep, currentPrice)}
              >
                {renderButtonText()}
              </Button>
              <div className="hidden md:flex items-center px-[14px] py-3 gap-[10px] rounded-2xl border border-sodaStreamBlue">
                <div>
                  <BrandFeatureIconSvg />
                </div>
                <p className="text-sodaStreamBlue font-[Poppins] text-[12px] font-medium leading-[17px]">
                  {quickconnectFeatureText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-[#F4F2EE] md:h-[500px] lg:h-[600px] w-full pb-[30px] md:pb-0 rounded-b-[60px]">
        <div className="container flex flex-col-reverse md:flex-row items-center md:justify-between">
          <div className="md:w-1/2 lg:w-5/12 text-center md:text-left mt-[30px] md:mt-0">
            <p className="text-[#002238] font-[Poppins] text-[19px] md:text-[23px] font-[500] leading-[29px] md:leading-[31px]">
              {datoProduct?.heroDescription}
            </p>
          </div>
          <div className="flex md:w-1/2 lg:w-2/5 md:h-[500px] lg:h-[600px]">
            <Image
              objectFit="contain"
              data={
                (datoProduct?.heroImage?.responsiveImage as ResponsiveImage) ??
                ""
              }
            />
          </div>
        </div>
      </div>
    </>
  );
};
